import { ChevronLeft, ChevronRight } from '@untitledui/icons'
import { useMemo } from 'react'
import { twJoin } from 'tailwind-merge'

import ProductDetailsCard from '@components/productDetails/ProductDetailsCard'
import ProductFinder from '@components/productFinder/ProductFinder'
import Product from '@components/productFinder/overlay/Product'
import ShelfDetails from '@components/shelfDetails/ShelfDetails'
import StoreMap from '@components/storeMap/StoreMap'
import { usePageHeader } from '@hooks/useHeader'
import { useIsWideLayout } from '@hooks/useMediaQuery'
import { useProductFinderContext } from '@routes/ProductFinder/Context'
import Provider from '@routes/ProductFinder/Provider'
import { usePlacementsByGtin } from '@state/product.ts'
import { stockLevelFacingProducer } from '@utils/facingProducer'

const Z_NUBSI = 'z-2'
const Z_LIST = 'z-1'

function ProductFinderRoute() {
  const isWideLayout = useIsWideLayout()

  return (
    <Provider>
      {
        isWideLayout ?
          <ProductFinderRouteDesktop /> :
          <ProductFinderRouteMobile />
      }
    </Provider>
  )
}

function ProductFinderRouteMobile() {
  const { searchState, setSearchState, selectedGTIN, selectedShelfId, setSelectedShelfId } = useProductFinderContext()

  const placementsByGtin = usePlacementsByGtin(selectedGTIN ?? undefined)

  const facingProducer = useMemo(
    () => stockLevelFacingProducer(selectedGTIN),
    [selectedGTIN]
  )

  usePageHeader({ isFullPage: searchState !== 'open' })

  const classNameProductFinder = twJoin(
    'z-2 flex w-full flex-col',
    searchState === 'initial' && 'absolute bottom-2 px-4',
    searchState !== 'initial' && 'h-full'
  )

  return (
    <div className='relative h-full'>
      <div className={classNameProductFinder}>
        <ProductFinder onBack={() => setSearchState('initial')} />
      </div>

      {searchState === 'initial' && (
        <div className='h-full pt-4'>
          <StoreMap
            showToolbar={true}
            highlightedShelves={placementsByGtin}
            selectedShelfId={selectedShelfId}
            setSelectedShelfId={setSelectedShelfId}
            dimType='3D'
          />
        </div>
      )}

      <ShelfDetails
        shelfId={selectedShelfId}
        onShelfIdChange={setSelectedShelfId}
        facingProducer={facingProducer}
      />
    </div>
  )
}

function ProductFinderRouteDesktop() {
  const { searchState, setSearchState, selectedGTIN, selectedShelfId, setSelectedShelfId } = useProductFinderContext()
  const placementsByGtin = usePlacementsByGtin(selectedGTIN ?? undefined)

  const facingProducer = useMemo(
    () => stockLevelFacingProducer(selectedGTIN),
    [selectedGTIN]
  )

  usePageHeader({ isFullPage: true })

  const classNameSearch = twJoin(
    Z_LIST,
    'w-1/3 xl:w-1/4',
    'flex flex-col px-6',
    searchState !== 'open' ? 'absolute top-8' : 'relative h-full pt-5 shadow-[3px_3px_34px_8px_#d6d6d6]'
  )

  const classNameMap = twJoin(
    'h-full',
    searchState === 'open' ? 'w-2/3 xl:w-3/4' : 'w-full'
  )

  return (
    <>
      <div className='flex size-full flex-row'>
        <div data-testid='product-finder-sidebar' className={classNameSearch} data-state={searchState}>
          <ProductFinder onBack={() => setSearchState('initial')} />
        </div>

        <div className={classNameMap}>
          <StoreMap
            showToolbar={true}
            selectedShelfId={selectedShelfId}
            setSelectedShelfId={setSelectedShelfId}
            highlightedShelves={placementsByGtin}
            dimType='3D'
            overlays3D={[
              <Product key="product-overlay" />,
            ]}
          />
        </div>

        {searchState !== 'initial' && (
          <Nubsi
            state={searchState}
            onClick={() => setSearchState(searchState === 'open' ? 'closed' : 'open')}
          />
        )}

        {selectedGTIN && <ProductDetailsCard />}
      </div>

      <ShelfDetails
        shelfId={selectedShelfId}
        onShelfIdChange={setSelectedShelfId}
        facingProducer={facingProducer}
      />
    </>
  )
}

interface NubsiProps {
  state: 'open' | 'closed'
  onClick: () => void
}

function Nubsi({ state, onClick }: NubsiProps) {
  const className = twJoin(
    state === 'open' ? 'left-1/3 xl:left-1/4' : 'left-0',
    Z_NUBSI,
    'absolute top-1/2 -translate-y-1/2',
    'rounded-r-lg bg-white',
    'border border-border-primary border-l-white',
    'cursor-pointer shadow-md',
    'flex h-12 w-4 items-center justify-center'
  )

  const ariaLabel = state === 'open' ? 'close search' : 'open search'

  return (
    <button
      type='button'
      className={className}
      onClick={onClick}
      aria-label={ariaLabel}
      data-testid="nubsi"
    >
      {state === 'open' ?
        <ChevronLeft size={15} aria-hidden /> :
        <ChevronRight size={15} aria-hidden />
      }
    </button>
  )
}

export default ProductFinderRoute
